import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { FETCH_STATUSES } from './constants';
import { hipNewUrl, lectioUrl, supoUrl } from '../../api';
import actionTypes from '../../constants/ActionTypes';
import { AxiosOptions } from './request';

export type UseFetchOptions = AxiosOptions & {
  key: string;
};
type FETCH_STATUS = keyof typeof FETCH_STATUSES;

/**
 * useFetch hooks for fetching data
 *
 * actual fetch operation is done in a middleware: src/app/utils/fetch/fetchMiddleware.ts
 * Results are set to the store at: src/app/utils/fetch/fetchReducer.ts
 *
 * Note: Fetch actions are differentiated using the `options.key` attribute
 * Therefore we should make sure to use unique values for the key.
 * It maybe worth using a uuid to do this automatically.
 * But that will make it harder to see the results in redux-dev-tools
 */
export function useFetch<T = any>(
  options: UseFetchOptions,
  baseUrl: string = ''
): [T, FETCH_STATUS, Error] {
  const dispatch = useDispatch();

  // Calling this will dispatch the FETCH redux action which will be caught by the fetchMiddleware
  const fetch = useCallback(
    () =>
      dispatch({
        type: actionTypes.FETCH,
        baseUrl,
        ...options
      }),
    [dispatch]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Grab the data from the redux store
  const selection = useSelector((state: any) => state.fetch[options.key]) || {
    data: null,
    status: FETCH_STATUSES.INIT,
    error: null
  };

  return [selection.data, selection.status, selection.error];
}

/**
 * useFetch exclusively for supo calls
 */
export function useSupo<T = any>(options: UseFetchOptions): [T, FETCH_STATUS, Error] {
  return useFetch(options, supoUrl);
}

/**
 * useFetch exclusively for hip-new calls
 */
export function useHipNew<T = any>(options: UseFetchOptions): [T, FETCH_STATUS, Error] {
  return useFetch(options, hipNewUrl);
}

/**
 * useFetch exclusively for lectio calls
 */
export function useLectio<T = any>(options: UseFetchOptions): [T, FETCH_STATUS, Error] {
  return useFetch(options, lectioUrl);
}
